import { createApp, watch } from "vue"; // Импортируем необходимые функции и компоненты из Vue
import App from "./App.vue"; // Основной компонент приложения
import router from "./router"; // Маршрутизатор для управления страницами приложения
import i18n from './plugins/i18n'; // Для локализации
import '@fortawesome/fontawesome-free/css/all.css'; // Подключаем стили FontAwesome
import '@fortawesome/fontawesome-free/js/all.js'; // Подключаем иконки FontAwesome
import { createPinia } from 'pinia'; // Система управления состоянием для Vue
import { createHead, useHead } from '@vueuse/head'; // Для динамического управления мета-данными страницы

// Инициализация необходимых плагинов и компонентов
const pinia = createPinia(); // Инициализируем Pinia для управления состоянием
const app = createApp(App); // Создаем приложение Vue с основным компонентом
const head = createHead(); // Инициализируем библиотеку для работы с мета-данными

// - - - - - - - - - - - - --- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 

// Загружаем сохраненную локаль из localStorage или устанавливаем 'en' по умолчанию
const savedLocale = localStorage.getItem("locale") || "en"; // Берем локаль из localStorage или по умолчанию 'en'
i18n.global.locale = savedLocale; // Устанавливаем локаль для i18n
localStorage.setItem("locale", savedLocale); // Сохраняем локаль в localStorage, чтобы использовать при следующем посещении

// - - - - - - - - - - - - --- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 

// Слушаем изменения локали, чтобы при изменении обновить маршруты и перезагрузить страницу с новой локалью
watch(
  () => i18n.global.locale, // Следим за изменением локали в i18n
  (newLocale) => {
    localStorage.setItem("locale", newLocale); // Сохраняем новую локаль в localStorage

    // Обновляем пути маршрутов с новой локалью без пересоздания маршрутизатора
    const routes = router.getRoutes(); // Получаем все маршруты из маршрутизатора
    routes.forEach(route => {
      // Если локализованный путь существует для этого маршрута
      if (i18n.global.te(`url.${route.name}`)) {
        const localizedPath = i18n.global.t(`url.${route.name}`); // Локализованный путь
        if (route.path !== `/${newLocale}/${localizedPath}`) {
          router.addRoute({
            ...route, // Копируем текущий маршрут
            path: `/${newLocale}/${localizedPath}` // Добавляем новый локализованный путь для текущего маршрута
          });
        }
      }
    });

    const currentRoute = router.currentRoute.value; // Получаем текущий маршрут
    if (currentRoute.name && i18n.global.te(`url.${currentRoute.name}`)) {
      const localizedPath = i18n.global.t(`url.${currentRoute.name}`); // Получаем локализованный путь для текущего маршрута
      router.push(`/${newLocale}/${localizedPath}`); // Перенаправляем на новый путь с локалью
    }
  },
  { immediate: true } // Выполняем сразу при инициализации
);

// - - - - - - - - - - - - --- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 

// Перехватываем каждый маршрут перед его переходом
router.beforeEach((to, from, next) => {
  // Если у маршрута есть мета-данные для title
  if (to.meta.title) {
    // Используем библиотеку useHead для динамического обновления title и мета-тегов
    useHead({
      title: i18n.global.t(to.meta.title),  // Устанавливаем локализованный заголовок страницы
      meta: to.meta.metaTags
        .filter(tag => tag.name || tag.property) // Фильтруем мета-теги, чтобы у них был хотя бы один атрибут (name или property)
        .map(tag => ({
          name: tag.name,
          property: tag.property,
          content: i18n.global.t(tag.content) // Применяем локализацию к контенту мета-тега
        }))
    });
  }
  next(); // Переход к следующему маршруту
});

// - - - - - - - - - - - - --- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 

// Инициализируем и монтируем приложение Vue с подключенными плагинами и маршрутами
app.use(router) // Подключаем маршрутизатор
   .use(i18n) // Подключаем плагин для локализации
   .use(pinia) // Подключаем Pinia для управления состоянием
   .use(head) // Подключаем библиотеку для управления мета-данными
   .mount("#app"); // Монтируем приложение на элемент с id="app"
